import { useState, useCallback } from 'react';
import { useMediaResource } from '@brightmls/nextgen.lae.flc';
import { MediaCategory, MediaResource } from '../../../../system/media';
import { useReso } from '../../../../contexts/ResoContext';
import { useMetadata } from '../../../../contexts/MetadataContext';
import { translateMediaRecord } from '../../../../system/media';
import { Logger } from '../../../../utils/logger';

export interface UseMediaSyncOptions {
  listingKey: string;
  onSyncComplete?: () => void;
  onSyncError?: (error: Error) => void;
  logger?: Logger;
}

/**
 * Hook to sync photo media from the server
 * Uses the updateMedia function from useSyncedMediaResource to replace local photo state
 * with fresh data from the server
 */
export const useMediaSync = (options: UseMediaSyncOptions) => {
  const {
    listingKey,
    onSyncComplete,
    onSyncError,
    logger: loggerProp,
  } = options;
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<Error | null>(null);
  const { executeQuery } = useReso();
  const { locale } = useMetadata();

  // Get updateMediaByCategory function from FLC hook to replace all photos
  const { updateMediaByCategory } = useMediaResource();

  // Default logger fallback
  const logger = loggerProp || {
    log: console.log,
    error: console.error,
    warn: console.warn,
  };

  /**
   * Fetch fresh photo media from server and update local state
   * Only syncs MediaCategory.Photo - does not affect documents, tours, or floor plans
   */
  const syncMedia = useCallback(async () => {
    if (!listingKey) {
      const error = new Error('No listing key provided for media sync');
      setSyncError(error);
      onSyncError?.(error);
      return;
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      logger.log('Starting media sync for listing:', listingKey);

      // Query photo media for this listing
      const result = await executeQuery<any>('CWA_Media', {
        queryParams: {
          $filter: `CWAResourceRecordKey eq '${listingKey}' and CWAMediaCategory eq 'Photo'`,
          $format: 'json',
        },
      });

      logger.log('CWA_Media query result:', result);

      if (!result || !result.value || !Array.isArray(result.value)) {
        throw new Error('Invalid response from media query');
      }

      logger.log(
        `Fetched ${result.value.length} photo media items from server for sync`,
      );

      // Get translation object
      const translation = locale();

      // Translate all photo media items
      const translatedMediaItems = result.value
        .filter((mediaItem: any) => mediaItem.CWAMediaCategory === 'Photo')
        .map((mediaItem: any) => translateMediaRecord(mediaItem, translation));

      logger.log(`Translated ${translatedMediaItems.length} photo media items`);

      // Replace all photo media with fresh data from server
      logger.log('Updating media context with synced photos...');
      updateMediaByCategory({
        category: MediaCategory.Photo,
        resources: translatedMediaItems as MediaResource[],
      });

      logger.log('Successfully synced photo media from server');
      onSyncComplete?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error('Failed to sync photo media:', err);
      setSyncError(err);
      onSyncError?.(err);
    } finally {
      setIsSyncing(false);
    }
  }, [
    listingKey,
    executeQuery,
    updateMediaByCategory,
    locale,
    onSyncComplete,
    onSyncError,
    logger,
  ]);

  return {
    syncMedia,
    isSyncing,
    syncError,
  };
};
